import { useEffect, useMemo } from 'react'
import { CheckIcon } from '@/components/CheckIcon'
import { Figure } from '@/components/Figure'
import { ProgressRing } from '@/components/ProgressRing'
import { getExercise } from '@/data/exercises'
import type { AfapPhase } from '@/domain/workout'
import { cx } from '@/lib/cx'
import { mmss } from '@/lib/format'
import { audioCues } from '@/services/audioCues'
import { selectStoredTicks, useSessionStore } from '@/store/sessionStore'
import type { PhaseRuntime } from './usePhaseRunner'
import styles from './workout.module.css'

interface AfapViewProps {
  phase: AfapPhase
  runtime: PhaseRuntime
}

/**
 * Reloj al alza: se marca cada bloque al terminarlo y el cronómetro se para
 * solo cuando cae el último.
 */
export function AfapView({ phase, runtime }: AfapViewProps) {
  const stored = useSessionStore(selectStoredTicks)
  const toggleTick = useSessionStore((state) => state.toggleTick)
  const completePhase = useSessionStore((state) => state.completePhase)

  const ticks = useMemo(
    () => phase.blocks.map((_, index) => stored?.[index] ?? false),
    [phase.blocks, stored],
  )

  const done = ticks.filter(Boolean).length
  const progress = phase.blocks.length === 0 ? 0 : done / phase.blocks.length
  const allDone = done === phase.blocks.length && phase.blocks.length > 0

  // Cerrar la lista es lo que cierra el bloque: se para el reloj al instante.
  useEffect(() => {
    if (!allDone || !runtime.running) return
    audioCues.play('finish')
    completePhase(runtime.elapsed)
  }, [allDone, runtime.running, runtime.elapsed, completePhase])

  return (
    <div className={styles.work}>
      <ProgressRing progress={progress} overall={runtime.overall} compact>
        <span className={styles.clockInner}>{mmss(runtime.elapsed)}</span>
      </ProgressRing>

      <div className={styles.status}>
        <div className={styles.statusLabel}>Lo más rápido que puedas</div>
      </div>

      <div className={styles.tasks}>
        {phase.blocks.map((movement, index) => {
          const exercise = getExercise(movement.exerciseId)
          const checked = ticks[index] ?? false
          return (
            <button
              key={`${movement.exerciseId}-${index}`}
              type="button"
              className={cx(styles.task, checked && styles.taskDone)}
              aria-pressed={checked}
              disabled={!runtime.running}
              onClick={() => {
                toggleTick(index)
                audioCues.play('tick')
              }}
            >
              <span className={styles.check}>
                <CheckIcon />
              </span>
              <b className={styles.taskReps}>{movement.reps}</b>
              <span className={styles.taskName}>
                {exercise.name}
                <i className={styles.taskEs}>{exercise.nameEs}</i>
              </span>
              {/* El monigote recuerda el movimiento sin tener que leer. */}
              <span className={styles.taskFigure}>
                <Figure exerciseId={movement.exerciseId} />
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
